import { PersonProfileService } from 'src/app/people/profile/person-profile.service';
import { AttributeDefinition } from '../models/attributes/attributeDefinition';
import { AttributeEditorMode } from '../models/attributes/attributeEditorMode';
import { AttributeWrite } from '../models/attributes/attributeWrite';
import { ComplexAttributeWrite } from '../models/attributes/complexAttributeWrite';
import { Seat } from '../models/team/seat';
import { CustomFieldsService } from './custom-fields.service';
import { TeamService } from './team.service';
import { Injectable } from '@angular/core';
import { CredentialSingletonService } from './credential-singleton.service';

export interface IGenericAttributeService {
  updateAttribute: (attribute: AttributeWrite) => Promise<boolean>;

  updateAttributeComplex: (attributes: ComplexAttributeWrite[]) => void;

  deleteAttribute: () => void;

  mode: AttributeEditorMode;
  entityId: string;
  attributeDefinition: AttributeDefinition;
}

@Injectable({
  providedIn: 'root',
})
export class GenericAttributeServiceFactory {
  constructor(
    private customFieldsService: CustomFieldsService,
    private personProfileService: PersonProfileService,
    private credentialSingletonService: CredentialSingletonService,
    private teamService: TeamService
  ) {}

  public BuildForTeamProfile = (
    teamId: string,
    attributeDefinition: AttributeDefinition,
    seat: Seat
  ): IGenericAttributeService =>
    new TeamUpdateAttributeService(teamId, attributeDefinition, seat, this.teamService);

  public BuildForPersonOnProfile = (
    personId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdatePersonAttributeService(personId, attributeDefinition, this.customFieldsService, () =>
      this.personProfileService.getPerson(personId)
    );

  public BuildForPersonOnEvidence = (
    personId: string,
    personScenarioId: string,
    personRequirementId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdatePersonAttributeService(
      personId,
      attributeDefinition,
      this.customFieldsService,
      () => {
        this.personProfileService.getPersonScenarioEvidences(
          personId,
          personScenarioId,
          personRequirementId
        );
        this.credentialSingletonService.queuePersonAvailableCredentialsAutomations();
      }
    );

  public BuildForPersonOnApplication = (
    personId: string,
    personCredentialApplicationId: string,
    attributeDefinition: AttributeDefinition
  ): IGenericAttributeService =>
    new UpdatePersonAttributeService(
      personId,
      attributeDefinition,
      this.customFieldsService,
      () => {
        this.personProfileService.getSpecialtyEvidence(personId, personCredentialApplicationId);
      }
    );
}

class TeamUpdateAttributeService implements IGenericAttributeService {
  constructor(
    private teamId: string,
    public attributeDefinition: AttributeDefinition,
    private seat: Seat,
    private teamService: TeamService
  ) {
    this.mode = AttributeEditorMode.TeamDetails;
    this.entityId = teamId;
  }
  updateAttribute = async (attribute: AttributeWrite) => {
    const result = await this.teamService.updateTrackedFieldValue(
      this.teamId,
      this.seat,
      attribute.attributeDefinitionId,
      attribute
    );

    if (result) {
      this.teamService.openSnackBar('Tracked field value has been updated.', true);
    }
    return result;
  };

  updateAttributeComplex = (_: ComplexAttributeWrite[]) => {
    throw Error('no complex attributes on teams');
  };

  deleteAttribute = () => {
    this.teamService
      .deleteTrackedFieldValue(this.teamId, this.seat, this.attributeDefinition.id)
      .subscribe(() => {
        this.teamService.openSnackBar('Tracked field value has been deleted.', true);
      });
  };
  mode: AttributeEditorMode;
  entityId: string;
}

class UpdatePersonAttributeService implements IGenericAttributeService {
  constructor(
    private personId: string,
    public attributeDefinition: AttributeDefinition,
    private customFieldsService: CustomFieldsService,
    private updateFunc: () => void
  ) {
    this.entityId = personId;
    this.mode = AttributeEditorMode.PersonDetails;
  }

  private onUpdate = (message: string) => {
    this.customFieldsService.openSnackBar(message, true);
    this.updateFunc();
  };

  updateAttribute = async (attribute: AttributeWrite) => {
    const result = await this.customFieldsService.updateAttribute(
      this.personId,
      this.attributeDefinition.id,
      attribute
    );
    if (result) {
      this.onUpdate('custom field value has been updated.');
      return true;
    }
    return false;
  };

  updateAttributeComplex = (attributes: ComplexAttributeWrite[]) =>
    this.customFieldsService
      .addOrUpdateComplexAttribute(this.personId, this.attributeDefinition.id, attributes)
      .subscribe(() => {
        this.onUpdate('custom field value has been updated.');
      });

  deleteAttribute = () =>
    this.customFieldsService
      .deleteAttribute(this.personId, this.attributeDefinition.id)
      .subscribe(() => {
        this.onUpdate('custom field value has been deleted.');
      });

  mode: AttributeEditorMode;
  entityId: string;
}
